import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import i18next from 'i18next';
import _ from 'lodash';
import moment from 'moment-timezone';
import { NUMBER_CONVERSIONS } from '@/main/app.constants';
import { AgentStatus, getAgentStatus } from '@/utilities/agents.utilities';
import { logError } from '@/utilities/logger';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { containsIgnoreCase } from '@/utilities/utilities';
import { sqAgentsApi } from '@seeqdev/api/endpoint/AgentsApi';
import { sqDatasourcesApi } from '@seeqdev/api/endpoint/DatasourcesApi';
import { sqFormulasApi } from '@seeqdev/api/endpoint/FormulasApi';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqRequestsApi } from '@seeqdev/api/endpoint/RequestsApi';
import { sqSCIMApi } from '@seeqdev/api/endpoint/SCIMApi';
import { sqSystemApi } from '@seeqdev/api/endpoint/SystemApi';
import { TypeEnum } from '@seeqdev/api/model/ConfigFieldV1';
import { SyncStatusEnum } from '@seeqdev/api/model/ConnectionStatusOutputV1';
import type { AxiosResponse } from 'axios';
import type { AgentStatusOutputV1 } from '@seeqdev/api/model/AgentStatusOutputV1';
import type { ComplexObjectConfigFieldV1 } from '@seeqdev/api/model/ComplexObjectConfigFieldV1';
import type { ConfigurationOptionOutputV1 } from '@seeqdev/api/model/ConfigurationOptionOutputV1';
import type { ConnectionInputV1 } from '@seeqdev/api/model/ConnectionInputV1';
import type { ConnectionOutputV1 } from '@seeqdev/api/model/ConnectionOutputV1';
import type { ConnectionStatusOutputV1 } from '@seeqdev/api/model/ConnectionStatusOutputV1';
import type { ConnectorOutputV1 } from '@seeqdev/api/model/ConnectorOutputV1';
import type { DatasourceSummaryStatusOutputV1 } from '@seeqdev/api/model/DatasourceSummaryStatusOutputV1';
import type { DatasourcesStatusOutputV1 } from '@seeqdev/api/model/DatasourcesStatusOutputV1';
import type { ScalarPropertyV1 } from '@seeqdev/api/model/ScalarPropertyV1';
import type { SecretConfigFieldV1 } from '@seeqdev/api/model/SecretConfigFieldV1';
import type { ConfigFieldV1 } from '@seeqdev/api/model/models';

type DataTableResponse = {
  data?: {
    table?: { headers?: Array<{ name?: string; type?: string }>; data?: Array<Array<string | number>> };
  };
};

/**
 * Fetch datasources immediately and log an error if the datasources could not be fetched.
 */
export function fetchDatasourcesImmediately(): Promise<DatasourcesStatusOutputV1 | undefined> {
  return sqAgentsApi
    .getDatasourcesStatus()
    .then(({ data: datasourcesStatus }) => datasourcesStatus)
    .catch((ex) => {
      logError(ex);
      return Promise.reject();
    });
}

/**
 * Returns the number of DISCONNECTED agents
 */
export function countDisconnectedAgents(agents: AgentStatusOutputV1[]): number {
  if (_.isNil(agents)) {
    return 0;
  }
  return _.countBy(agents, (agent) => agent.status === SeeqNames.Connectors.Connections.Status.Disconnected).true || 0;
}

/**
 * Returns true if all agents associated with the datasource's connections are disconnected
 */
export function hasAllAgentsDisconnected(
  datasource: DatasourceSummaryStatusOutputV1,
  agents: AgentStatusOutputV1[],
): boolean {
  const connections = datasource.connections || [];
  if (connections.length === 0) {
    return false;
  }
  return connections.every((conn) => {
    const agent = agents.find((a) => a.name === conn.agentName);
    const isConnected = agent && getAgentStatus(agent) === AgentStatus.Connected;
    return !isConnected;
  });
}

const hasValue = (fieldValue: unknown): boolean => !_.isEmpty(fieldValue);

export function getDatasourceFilterParameters(
  searchParams: Partial<DatasourceFilterParameters>,
): DatasourceFilterParameters {
  const filterParams: DatasourceFilterParameters = {
    name: '',
    datasourceClass: '',
    datasourceId: '',
    agentName: '',
    status: '',
    metricsTimeRange: `3 ${i18next.t('ADMIN.DATASOURCES.TIME_RANGE_DEFAULT_UNITS')}`,
    datasourceLabels: [],
  };

  for (const key of Object.keys(filterParams) as Array<keyof DatasourceFilterParameters>) {
    if (key in searchParams) {
      const value = searchParams[key];
      if (key === 'datasourceLabels') {
        filterParams.datasourceLabels = Array.isArray(value)
          ? (value as string[])
          : _.isString(value) && value
            ? value.split(',')
            : [];
      } else if (Array.isArray(value)) {
        filterParams[key] = value.join(',') as DatasourceFilterParameters[typeof key];
      } else if (!_.isNil(value) && typeof value !== 'boolean') {
        filterParams[key] = value as DatasourceFilterParameters[typeof key];
      }
    }
  }

  return filterParams;
}

/**
 * Returns true if the filter parameters (excluding status) are empty
 */
export function isFilterEmpty(filterParams: DatasourceFilterParameters): boolean {
  return (
    _.isEmpty(filterParams.name) &&
    _.isEmpty(filterParams.datasourceClass) &&
    _.isEmpty(filterParams.datasourceId) &&
    _.isEmpty(filterParams.agentName) &&
    _.isEmpty(filterParams.datasourceLabels)
  );
}

/**
 * Filters the list of datasources by the filter parameters provided and returns them in alphabetical order
 */
export function filterAndSortDatasources(
  datasources: DatasourceSummaryStatusOutputV1[],
  filterParams: DatasourceFilterParameters,
): DatasourceSummaryStatusOutputV1[] | null {
  if (_.isNil(datasources)) {
    return null;
  }

  const datasourceClassFilterMap: Record<string, string[]> = {
    'CONNECT Data Services': ['CONNECT Data Services', 'AVEVA Data Hub'],
  };

  let filteredDatasources = datasources;

  if (hasValue(filterParams.name)) {
    filteredDatasources = _.filter(filteredDatasources, (ds) => containsIgnoreCase(ds.name ?? '', filterParams.name));
  }

  if (hasValue(filterParams.datasourceClass)) {
    const allowedClasses = datasourceClassFilterMap[filterParams.datasourceClass] || [filterParams.datasourceClass];
    filteredDatasources = _.filter(filteredDatasources, (ds) =>
      _.some(allowedClasses, (allowedClass) => containsIgnoreCase(ds.datasourceClass ?? '', allowedClass)),
    );
  }

  if (hasValue(filterParams.datasourceId)) {
    filteredDatasources = _.filter(filteredDatasources, (ds) =>
      containsIgnoreCase(ds.datasourceId ?? '', filterParams.datasourceId),
    );
  }

  if (hasValue(filterParams.agentName)) {
    filteredDatasources = _.filter(
      filteredDatasources,
      (ds) =>
        _.countBy(ds.connections, (conn) => containsIgnoreCase(conn.agentName ?? '', filterParams.agentName)).true > 0,
    );
  }

  if (hasValue(filterParams.status)) {
    filteredDatasources = _.filter(filteredDatasources, (ds) => {
      if (filterParams.status === 'INDEXING') {
        if (isIndexing(ds)) {
          return true;
        }
      }

      return _.countBy(ds.connections, (conn) => conn.status === filterParams.status).true > 0;
    });
  }

  if (hasValue(filterParams.datasourceLabels)) {
    filteredDatasources = _.filter(filteredDatasources, (ds) =>
      _.every(filterParams.datasourceLabels, (label) =>
        _.includes(parseDatasourceLabelsProperty(ds.datasourceLabels), label),
      ),
    );
  }

  return _.sortBy(filteredDatasources, [
    (ds) => (ds.datasourceClass ?? '').toLowerCase(),
    (ds) => (ds.name ?? '').toLowerCase(),
  ]);
}

export function getDatasourceStatus(datasource: DatasourceSummaryStatusOutputV1): DatasourceStatus {
  if (isPlaceholder(datasource) && isNew(datasource)) {
    return DatasourceStatus.New;
  } else if (isError(datasource)) {
    return DatasourceStatus.Error;
  } else if (isIndexing(datasource)) {
    return DatasourceStatus.Indexing;
  } else if (isWarning(datasource)) {
    return DatasourceStatus.Warning;
  } else if (isHappy(datasource)) {
    return DatasourceStatus.Happy;
  } else if (isNotConnectable(datasource)) {
    return DatasourceStatus.NotConnectable;
  } else {
    return DatasourceStatus.Unknown;
  }
}

export function isPlaceholder(ds: DatasourceSummaryStatusOutputV1): boolean {
  return ds.placeholder === true;
}

export const datasourceClassMap: Record<string, string> = {
  'AVEVA Data Hub': 'CONNECT Data Services',
};

export function datasourceDisplayName(ds: DatasourceSummaryStatusOutputV1): string {
  const dsClass = ds.datasourceClass ?? '';
  const mappedClassName = datasourceClassMap[dsClass] || dsClass;
  const dsClassName = isNew(ds)
    ? mappedClassName.replace(/Invalid (.*) Connection/, 'Pending $1 Connection')
    : mappedClassName;

  const displayDsClass = isPlaceholder(ds) ? `[${dsClassName}]` : dsClassName;
  return `${displayDsClass}: ${ds.name}`;
}

const isError = (ds: DatasourceSummaryStatusOutputV1): boolean => {
  const totalConnectionsCount = ds.totalConnectionsCount ?? 0;
  const connectionsConnectedCount = ds.connectionsConnectedCount ?? 0;
  return hasTooManyConnections(ds) || (connectionsConnectedCount === 0 && totalConnectionsCount > 0);
};

export function hasTooManyConnections(ds: DatasourceSummaryStatusOutputV1): boolean {
  // Ignition gateway is allowed to have more than 1 connection
  const isIgnitionGateway = ds.datasourceClass === SeeqNames.RemoteDatasourceClasses.Ignition;
  return !isIgnitionGateway && (ds.connections?.length ?? 0) > 1;
}

export function isIndexing(ds: DatasourceSummaryStatusOutputV1): boolean {
  return _.includes(
    [SyncStatusEnum.ARCHIVINGDELETEDITEMS, SyncStatusEnum.INPROGRESS, SyncStatusEnum.INITIALIZING],
    ds.syncStatus,
  );
}

export function latestOf(moments: moment.Moment[]): moment.Moment {
  let latest = _.isEmpty(moments) ? moment(0) : moments[0];
  moments.forEach((thisMoment) => {
    latest = latest.isBefore(thisMoment) ? thisMoment : latest;
  });
  return latest;
}

export function isIndexingProgressing(
  referenceTime: moment.Moment | undefined,
  indexingNoProgressLimit: moment.Duration,
): boolean {
  return moment().isBefore((referenceTime ?? moment(0)).clone().add(indexingNoProgressLimit));
}

const isWarning = (ds: DatasourceSummaryStatusOutputV1): boolean => {
  const totalConnectionsCount = ds.totalConnectionsCount ?? 0;
  const connectionsConnectedCount = ds.connectionsConnectedCount ?? 0;
  return connectionsConnectedCount > 0 && connectionsConnectedCount < totalConnectionsCount;
};

const isHappy = (ds: DatasourceSummaryStatusOutputV1): boolean => {
  const totalConnectionsCount = ds.totalConnectionsCount ?? 0;
  const connectionsConnectedCount = ds.connectionsConnectedCount ?? 0;
  return connectionsConnectedCount === totalConnectionsCount && totalConnectionsCount > 0;
};

const isNotConnectable = (ds: DatasourceSummaryStatusOutputV1): boolean => {
  const totalConnectionsCount = ds.totalConnectionsCount ?? 0;
  const connectionsConnectedCount = ds.connectionsConnectedCount ?? 0;
  return !isNew(ds) && connectionsConnectedCount === 0 && totalConnectionsCount === 0;
};

function isNew(datasource: DatasourceSummaryStatusOutputV1) {
  const isNewLimit = moment.duration(30, 'seconds');
  const firstConnection = datasource.connections?.[0];
  return _.isNil(firstConnection?.createdAt)
    ? false
    : moment().subtract(isNewLimit).isBefore(moment(firstConnection.createdAt));
}

export function isCompletelyDisabled(datasource: DatasourceSummaryStatusOutputV1): boolean {
  return _.every(datasource.connections ?? [], (conn) => getConnectionStatus(conn) === ConnectionStatus.Disabled);
}

/**
 * Cancels all requests to the selected datasource
 */
export function cancelAllRequests(datasource: DatasourceSummaryStatusOutputV1): Promise<void> {
  const datasourceClass = datasource.datasourceClass;
  const datasourceId = datasource.datasourceId;
  return sqRequestsApi
    .cancelRequests({ datasourceClass, datasourceId })
    .then(() => {
      successToast({
        messageKey: 'ADMIN.DATASOURCE.CANCELED_ALL_SUCCESS',
      });
    })
    .catch((error: any) => {
      errorToast({ httpResponseOrError: error });
    });
}

/**
 * Request indexing of the first non DISABLED connection or reports the error given as parameter.
 */
export function requestIndex(datasource: DatasourceSummaryStatusOutputV1): Promise<void> {
  return sqAgentsApi
    .index(
      {
        syncMode: 'FULL',
      },
      {
        datasourceClass: datasource.datasourceClass,
        datasourceId: datasource.datasourceId,
      },
    )
    .then(() => {
      successToast({
        messageKey: 'ADMIN.DATASOURCES.REQUESTED_INDEX_SUCCESS',
        messageParams: {
          datasourceClass: datasource.datasourceClass,
          datasourceId: datasource.datasourceId,
        },
      });
    })
    .catch((error: any) => {
      errorToast({ httpResponseOrError: error, displayForbidden: true });
    });
}

export function setDatasourceAllowRequests(
  datasource: DatasourceSummaryStatusOutputV1,
  allowRequests: boolean,
): Promise<void> {
  return sqItemsApi
    .setProperty({ value: allowRequests }, { id: datasource.id, propertyName: SeeqNames.Properties.Enabled })
    .then(() => {
      const newStatus = allowRequests
        ? 'ADMIN.DATASOURCES.ALLOWS_REQUESTS'
        : 'ADMIN.DATASOURCES.DOES_NOT_ALLOW_REQUESTS';
      const messageParams = {
        datasourceName: datasource.name,
        datasourceId: datasource.datasourceId,
      };
      successToast({ messageKey: newStatus, messageParams });
    })
    .catch((error: any) => {
      errorToast({ httpResponseOrError: error, displayForbidden: true });
    });
}

/**
 * This method exists to provide a mechanism to return pages of results sequentially rather than all at once. The
 * motivating use case was to pass a very large list of filters to the GET /items request, since it is possible for
 * the URL to be too long to be handled correctly:
 * {@link https://stackoverflow.com/questions/812925/what-is-the-maximum-possible-length-of-a-query-string}
 * Some customers have hundreds of datasources, so to compute the datasource metrics could require thousands
 * of signals, each of which requires a very long filter to look up.  The sequential pagination may make the lookup
 * take a bit longer but avoids slamming the API with dozens of concurrent requests.
 *
 * @template InputType
 * @template OutputType
 * @param {InputType[]} input - An array on which {@link fn} acts
 * @param {number} pageSize - The size to use for pages of {@link input}
 * @param {(arr: InputType[]) => Promise<OutputType[]>} fn - A function that acts on a page of the {@link input}
 * array to produce a promise that resolves to a page of {@link OutputType} objects
 * @returns {Promise<OutputType[]>} - A promise that resolves to an array of {@link OutputType} objects
 */
export function pagePromises<InputType, OutputType>(
  input: InputType[],
  pageSize: number,
  fn: (arr: InputType[]) => Promise<OutputType[]>,
): Promise<OutputType[]> {
  function pagedPromises<InputType, OutputType>(
    input: InputType[],
    output: OutputType[],
    pageSize: number,
    fn: (arr: InputType[]) => Promise<OutputType[]>,
  ): Promise<OutputType[]> {
    return pageSize > input.length
      ? fn(input).then((result) => _.concat(output, result))
      : fn(_.slice(input, 0, pageSize))
          .then((result) => {
            return Promise.all([result, pagedPromises(_.slice(input, pageSize), output, pageSize, fn)]);
          })
          .then(([page, morePages]) => _.concat(page, morePages));
  }

  return pagedPromises(input, [] as OutputType[], pageSize, fn);
}

export function getMonitorsDatasourceConfig(): Promise<ConfigurationOptionOutputV1[]> {
  return sqSystemApi
    .getConfigurationOptions({ offset: 0, limit: 10000 })
    .then(({ data }) =>
      _.filter(data.configurationOptions, (option) => _.startsWith(option.path, 'Features/Monitors/Datasource')),
    );
}

function getOrDefault(configParam?: ConfigurationOptionOutputV1): unknown {
  return configParam?.value ?? configParam?.defaultValue;
}

function getMonitorsDatasourceId(monitorsDatasourceConfig: ConfigurationOptionOutputV1[]): string {
  const machineName = String(
    getOrDefault(
      _.find(monitorsDatasourceConfig, (option) => option.path === 'Features/Monitors/Datasource/MachineName'),
    ) ?? '',
  );
  return `monitors-${machineName}`;
}

function isMonitorsDatasourceEnabled(monitorsDatasourceConfig: ConfigurationOptionOutputV1[]): boolean {
  return (
    getOrDefault(
      _.find(monitorsDatasourceConfig, (option) => option.path === 'Features/Monitors/Datasource/Enabled'),
    ) === true
  );
}

function isMonitorsImportFrequencyOk(monitorsDatasourceConfig: ConfigurationOptionOutputV1[]): boolean {
  const importFrequency = getOrDefault(
    _.find(monitorsDatasourceConfig, (option) => option.path === 'Features/Monitors/Datasource/ImportFrequency'),
  );
  return _.isNumber(importFrequency) && moment.duration(importFrequency, 'seconds') <= moment.duration(15, 'minutes');
}

export function confirmMonitorsDatasourceExists(configurationOptions: ConfigurationOptionOutputV1[]): Promise<boolean> {
  return sqDatasourcesApi
    .getDatasources({
      datasourceClass: SeeqNames.LocalDatasources.Monitors.DatasourceClass,
      datasourceId: getMonitorsDatasourceId(configurationOptions),
    })
    .then(({ data: { datasources } }) => (datasources ?? []).length === 1);
}

export function canComputeMetrics(
  monitorsDatasourceAvailable: boolean,
  configurationOptions: ConfigurationOptionOutputV1[],
): boolean {
  return (
    monitorsDatasourceAvailable &&
    isMonitorsDatasourceEnabled(configurationOptions) &&
    isMonitorsImportFrequencyOk(configurationOptions)
  );
}

export function getMetricsTooltip(
  monitorsDatasourceAvailable: boolean,
  monitorsDatasourceConfig: ConfigurationOptionOutputV1[],
): string {
  if (!isMonitorsDatasourceEnabled(monitorsDatasourceConfig)) {
    return 'ADMIN.DATASOURCES.FILTER.MONITORS_DATASOURCE_DISABLED';
  } else if (!monitorsDatasourceAvailable) {
    return 'ADMIN.DATASOURCES.FILTER.MONITORS_DATASOURCE_NOT_FOUND';
  } else if (!isMonitorsImportFrequencyOk(monitorsDatasourceConfig)) {
    return 'ADMIN.DATASOURCES.FILTER.MONITORS_IMPORT_FREQUENCY_TOO_LOW';
  }
  return 'ADMIN.DATASOURCES.FILTER.METRICS_TIME_RANGE_TOOLTIP';
}

function createZeroDatasourceMetrics(seeqId: string): DatasourceMetrics {
  return {
    DatasourceSeeqId: seeqId,
    SuccessCount: 0,
    FailureCount: 0,
    SampleCount: 0,
    CapsuleCount: 0,
    AverageRequestTime: 0,
    TotalRequestCount: 0,
  };
}

/**
 * Merges 24h and 7d metric rows by datasource id. Fills a window with zeroed metrics
 * if that window is missing, so a partial failure in one `updateMetricsInternal` call
 * can still return data for the other window.
 */
export function buildDatasourceMetricsMap(
  filteredDatasources: DatasourceSummaryStatusOutputV1[],
  metrics24h: DatasourceMetrics[] | undefined,
  metrics7d: DatasourceMetrics[] | undefined,
): Map<string, DatasourceMetricsContainer> {
  const metrics24hByDatasourceId = new Map(
    (metrics24h ?? []).map((m) => [m.DatasourceSeeqId, m] as [string, DatasourceMetrics]),
  );
  const metrics7dByDatasourceId = new Map(
    (metrics7d ?? []).map((m) => [m.DatasourceSeeqId, m] as [string, DatasourceMetrics]),
  );
  const datasourceMetricsById = new Map<string, DatasourceMetricsContainer>();

  filteredDatasources.forEach((datasource) => {
    const datasourceId = datasource.id;
    if (!datasourceId) {
      return;
    }
    const last24Hours = metrics24hByDatasourceId.get(datasourceId) ?? createZeroDatasourceMetrics(datasourceId);
    const last7Days = metrics7dByDatasourceId.get(datasourceId) ?? createZeroDatasourceMetrics(datasourceId);
    datasourceMetricsById.set(datasourceId, {
      Last24Hours: {
        DatasourceSeeqId: last24Hours.DatasourceSeeqId,
        SuccessCount: last24Hours.SuccessCount,
        FailureCount: last24Hours.FailureCount,
        SampleCount: last24Hours.SampleCount,
        CapsuleCount: last24Hours.CapsuleCount,
        AverageRequestTime: last24Hours.AverageRequestTime,
        TotalRequestCount: last24Hours.TotalRequestCount,
      },
      LastWeek: {
        DatasourceSeeqId: last7Days.DatasourceSeeqId,
        SuccessCount: last7Days.SuccessCount,
        FailureCount: last7Days.FailureCount,
        SampleCount: last7Days.SampleCount,
        CapsuleCount: last7Days.CapsuleCount,
        AverageRequestTime: last7Days.AverageRequestTime,
        TotalRequestCount: last7Days.TotalRequestCount,
      },
    });
  });
  return datasourceMetricsById;
}

export function getUpdatedMetrics(
  filteredDatasources: DatasourceSummaryStatusOutputV1[],
  monitorsDatasourceConfig: ConfigurationOptionOutputV1[],
): Promise<DatasourcesMetrics> {
  const monitorsDatasourceId = getMonitorsDatasourceId(monitorsDatasourceConfig);

  if (_.isEmpty(filteredDatasources)) {
    return Promise.resolve({ metrics: new Map() });
  }

  const duration24Hours = moment.duration(24, 'hours');
  const duration1Week = moment.duration(7, 'days');

  return Promise.all([
    updateMetricsInternal(filteredDatasources, duration24Hours, monitorsDatasourceId),
    updateMetricsInternal(filteredDatasources, duration1Week, monitorsDatasourceId),
  ]).then(([metrics24h, metricsWeek]) => {
    return {
      metrics: buildDatasourceMetricsMap(filteredDatasources, metrics24h, metricsWeek),
    };
  });
}

function getDataId(datasource: DatasourceSummaryStatusOutputV1, relativePath: string): string {
  const dsClass = (datasource.datasourceClass ?? '').replaceAll('.', '_');
  const dsId = (datasource.datasourceId ?? '').replaceAll('.', '_');
  const dsName = (datasource.name ?? '').replaceAll('.', '_');
  return `Datasource/${dsClass}/${dsName} - ${dsId}/${relativePath}`;
}

export function updateMetricsInternal(
  datasources: DatasourceSummaryStatusOutputV1[],
  duration: moment.Duration,
  monitorsDsId: string,
): Promise<DatasourceMetrics[] | undefined> {
  // This string will be used in a Seeq formula, so no translation is needed
  const durationSeconds = `${duration.asSeconds()} seconds`;
  const monitorsDsClass = SeeqNames.LocalDatasources.Monitors.DatasourceClass;

  const parameterFilters = _.flatMap(datasources, (datasource) => {
    const getSearchFilter = (dataId: string) => {
      return `Datasource Class==${monitorsDsClass} && Datasource ID==${monitorsDsId} && Data ID==${dataId}`;
    };
    return [
      getSearchFilter(getDataId(datasource, DATASOURCE_MONITORS_METRICS_DATA_IDS.RxSuccessesMeter)),
      getSearchFilter(getDataId(datasource, DATASOURCE_MONITORS_METRICS_DATA_IDS.RxFailuresMeter)),
      getSearchFilter(getDataId(datasource, DATASOURCE_MONITORS_METRICS_DATA_IDS.RxSamplesMeter)),
      getSearchFilter(getDataId(datasource, DATASOURCE_MONITORS_METRICS_DATA_IDS.RxCapsulesMeter)),
      getSearchFilter(getDataId(datasource, DATASOURCE_MONITORS_METRICS_DATA_IDS.RequestTimeTimerMean)),
      getSearchFilter(getDataId(datasource, DATASOURCE_MONITORS_METRICS_DATA_IDS.RequestTimeTimerCount)),
    ];
  });

  const parameterSearch = (filters: string[]): Promise<Array<{ searchFilter: string; seeqId: string | undefined }>> =>
    sqItemsApi.searchItems({ filters }).then(({ data }) => {
      const results = _.map(filters, (filter) => {
        const foundItem = _.find(data.items, (item) => {
          const dataIdMatch = filter.match(/Data ID==(.+)$/);
          if (!dataIdMatch) return false;

          const expectedDataId = dataIdMatch[1];

          // We match using the Data ID value directly because item.name stores the full Data ID path.
          // Reconstructing the path from ancestors is unreliable (ancestors use "/" separators while Data IDs may include ".").
          // Since our filters end with `Data ID==...`, extract that value and match against the item name.
          return item.name === expectedDataId;
        });

        return {
          searchFilter: filter,
          seeqId: foundItem?.id,
        };
      });

      return results;
    });

  return pagePromises(parameterFilters, 40, parameterSearch).then((results) =>
    runMetricTableFormulas(results, datasources, durationSeconds),
  );
}

function runMetricTableFormulas(
  inputs: { searchFilter: string; seeqId: string | undefined }[],
  datasources: DatasourceSummaryStatusOutputV1[],
  durationSeconds: string,
): Promise<DatasourceMetrics[] | undefined> {
  const countingTableFormula = (
    datasources: DatasourceSummaryStatusOutputV1[],
    signalIdProvider: (datasource: DatasourceSummaryStatusOutputV1) => string | undefined,
    durationSeconds: string,
  ): string => {
    const countFormula = (datasource: DatasourceSummaryStatusOutputV1, index: number) => {
      const signalId = signalIdProvider(datasource);
      return _.isUndefined(signalId)
        ? '0'
        : `$signal${index}.runningDelta().max(0).sum(capsule(now().subtract(${durationSeconds}), now()))`;
    };
    const addColumnText = _.map(
      datasources,
      (datasource, index) => `.addColumn('${datasource.id}', $id -> ${countFormula(datasource, index)})`,
    ).join('');
    return `toTable('Metric', 'Count')${addColumnText}`;
  };

  const countingTableParameters = (
    datasources: DatasourceSummaryStatusOutputV1[],
    signalIdProvider: (datasource: DatasourceSummaryStatusOutputV1) => string | undefined,
  ): string[] => {
    const countFormulaParameters = (datasource: DatasourceSummaryStatusOutputV1, index: number) => {
      const signalId = signalIdProvider(datasource);
      return _.isUndefined(signalId) ? [] : [`signal${index}=${signalId}`];
    };
    return _.flatMap(datasources, (datasource: DatasourceSummaryStatusOutputV1, index) =>
      countFormulaParameters(datasource, index),
    );
  };

  const successCountSignalProvider = (datasource: DatasourceSummaryStatusOutputV1): string | undefined => {
    return _.find(inputs, (r) =>
      _.endsWith(r.searchFilter, getDataId(datasource, DATASOURCE_MONITORS_METRICS_DATA_IDS.RxSuccessesMeter)),
    )?.seeqId;
  };
  const successCountTableFormula = sqFormulasApi.runFormula({
    formula: countingTableFormula(datasources, successCountSignalProvider, durationSeconds),
    parameters: countingTableParameters(datasources, successCountSignalProvider),
  });

  const failureCountSignalProvider = (datasource: DatasourceSummaryStatusOutputV1): string | undefined => {
    return _.find(inputs, (r) =>
      _.endsWith(r.searchFilter, getDataId(datasource, DATASOURCE_MONITORS_METRICS_DATA_IDS.RxFailuresMeter)),
    )?.seeqId;
  };
  const failureCountTableFormula = sqFormulasApi.runFormula({
    formula: countingTableFormula(datasources, failureCountSignalProvider, durationSeconds),
    parameters: countingTableParameters(datasources, failureCountSignalProvider),
  });

  const sampleCountSignalProvider = (datasource: DatasourceSummaryStatusOutputV1): string | undefined => {
    return _.find(inputs, (r) =>
      _.endsWith(r.searchFilter, getDataId(datasource, DATASOURCE_MONITORS_METRICS_DATA_IDS.RxSamplesMeter)),
    )?.seeqId;
  };
  const sampleCountTableFormula = sqFormulasApi.runFormula({
    formula: countingTableFormula(datasources, sampleCountSignalProvider, durationSeconds),
    parameters: countingTableParameters(datasources, sampleCountSignalProvider),
  });

  const capsuleCountSignalProvider = (datasource: DatasourceSummaryStatusOutputV1): string | undefined => {
    return _.find(inputs, (r) =>
      _.endsWith(r.searchFilter, getDataId(datasource, DATASOURCE_MONITORS_METRICS_DATA_IDS.RxCapsulesMeter)),
    )?.seeqId;
  };
  const capsuleCountTableFormula = sqFormulasApi.runFormula({
    formula: countingTableFormula(datasources, capsuleCountSignalProvider, durationSeconds),
    parameters: countingTableParameters(datasources, capsuleCountSignalProvider),
  });

  const requestTimeMeanSignalProvider = (datasource: DatasourceSummaryStatusOutputV1): string | undefined => {
    return _.find(inputs, (r) =>
      _.endsWith(r.searchFilter, getDataId(datasource, DATASOURCE_MONITORS_METRICS_DATA_IDS.RequestTimeTimerMean)),
    )?.seeqId;
  };

  const requestTimeCountSignalProvider = (datasource: DatasourceSummaryStatusOutputV1): string | undefined => {
    return _.find(inputs, (r) =>
      _.endsWith(r.searchFilter, getDataId(datasource, DATASOURCE_MONITORS_METRICS_DATA_IDS.RequestTimeTimerCount)),
    )?.seeqId;
  };

  const totalRequestCountTableFormula = sqFormulasApi.runFormula({
    formula: countingTableFormula(datasources, requestTimeCountSignalProvider, durationSeconds),
    parameters: countingTableParameters(datasources, requestTimeCountSignalProvider),
  });

  const weightedRequestTimeSumFormula = (
    datasources: DatasourceSummaryStatusOutputV1[],
    meanSignalProvider: (datasource: DatasourceSummaryStatusOutputV1) => string | undefined,
    countSignalProvider: (datasource: DatasourceSummaryStatusOutputV1) => string | undefined,
    durationSeconds: string,
  ): string => {
    const window = `capsule(now().subtract(${durationSeconds}), now())`;
    const colExpr = (datasource: DatasourceSummaryStatusOutputV1, index: number) => {
      const meanId = meanSignalProvider(datasource);
      const countId = countSignalProvider(datasource);
      if (_.isUndefined(meanId) || _.isUndefined(countId)) return '0';
      return `($mean${index} * $count${index}.runningDelta().max(0)).sum(${window})`;
    };
    const addColumnText = _.map(
      datasources,
      (datasource, index) => `.addColumn('${datasource.id}', $id -> ${colExpr(datasource, index)})`,
    ).join('');
    return `toTable('Metric', 'WeightedSum')${addColumnText}`;
  };

  const weightedRequestTimeSumParameters = (
    datasources: DatasourceSummaryStatusOutputV1[],
    meanSignalProvider: (datasource: DatasourceSummaryStatusOutputV1) => string | undefined,
    countSignalProvider: (datasource: DatasourceSummaryStatusOutputV1) => string | undefined,
  ): string[] =>
    _.flatMap(datasources, (datasource, index) => {
      const meanId = meanSignalProvider(datasource);
      const countId = countSignalProvider(datasource);
      if (_.isUndefined(meanId) || _.isUndefined(countId)) return [];
      return [`mean${index}=${meanId}`, `count${index}=${countId}`];
    });

  const requestTimeMeanTableFormula = sqFormulasApi.runFormula({
    formula: weightedRequestTimeSumFormula(
      datasources,
      requestTimeMeanSignalProvider,
      requestTimeCountSignalProvider,
      durationSeconds,
    ),
    parameters: weightedRequestTimeSumParameters(
      datasources,
      requestTimeMeanSignalProvider,
      requestTimeCountSignalProvider,
    ),
  });

  return Promise.all([
    successCountTableFormula,
    failureCountTableFormula,
    sampleCountTableFormula,
    capsuleCountTableFormula,
    totalRequestCountTableFormula,
    requestTimeMeanTableFormula,
  ])
    .then(
      ([
        successCountTableData,
        failureCountTableData,
        sampleCountTableData,
        capsuleCountTableData,
        totalRequestCountTableData,
        requestTimeMeanTableData,
      ]) => {
        const dataFromTable = (tableResponse: AxiosResponse<any> | DataTableResponse) => {
          const headers = tableResponse.data?.table?.headers ?? [];
          const dataRow = (tableResponse.data?.table?.data?.[0] ?? []) as Array<string | number | undefined>;
          const entries = headers
            .slice(1)
            .map((datasourceSeeqId: { name?: string; type?: string }, index: number) => [
              datasourceSeeqId?.name ?? '',
              dataRow.slice(1)[index],
            ]);
          return Object.fromEntries(entries);
        };

        const successMap = dataFromTable(successCountTableData as DataTableResponse);
        const failureMap = dataFromTable(failureCountTableData as DataTableResponse);
        const samplesMap = dataFromTable(sampleCountTableData as DataTableResponse);
        const capsulesMap = dataFromTable(capsuleCountTableData as DataTableResponse);
        const totalRequestsMap = dataFromTable(totalRequestCountTableData as DataTableResponse);
        const weightedRequestTimeSumMap = dataFromTable(requestTimeMeanTableData as DataTableResponse);

        return _.map(datasources, (datasource) => {
          const id = datasource.id;
          const successCount = (successMap[id] as number) ?? 0;
          const failureCount = (failureMap[id] as number) ?? 0;
          const samplesCount = (samplesMap[id] as number) ?? 0;
          const capsulesCount = (capsulesMap[id] as number) ?? 0;
          const timerRequestCount = (totalRequestsMap[id] as number) ?? 0;
          const outcomeRequestTotal = successCount + failureCount;

          const totalRequestsCount = outcomeRequestTotal > 0 ? outcomeRequestTotal : timerRequestCount;
          const weightedSumMs = (weightedRequestTimeSumMap[id] as number) ?? 0;

          const averageRequestTimeMs = timerRequestCount > 0 ? weightedSumMs / timerRequestCount : 0;

          return {
            DatasourceSeeqId: id,
            SuccessCount: successCount,
            FailureCount: failureCount,
            SampleCount: samplesCount,
            CapsuleCount: capsulesCount,
            TotalRequestCount: totalRequestsCount,
            AverageRequestTime: averageRequestTimeMs,
          } as DatasourceMetrics;
        });
      },
    )
    .catch(() => {
      errorToast({ messageKey: 'ADMIN.DATASOURCES.METRICS.CALCULATION_ERROR' });
      return undefined;
    });
}

export function setConnectionEnabled(connection: ConnectionStatusOutputV1, connectionEnabled: boolean): Promise<void> {
  const connectorName = connection.connectorName as string;
  const connectionKey = {
    agentName: connection.agentName,
    connectionName: connection.name as string,
    connectorName,
  };

  return sqAgentsApi
    .getConnection(connectionKey)
    .then(({ data }) =>
      sqAgentsApi.createOrUpdateConnection(
        {
          datasourceId: data.datasourceId,
          enabled: connectionEnabled,
          json: data.json,
          maxConcurrentRequests: data.maxConcurrentRequests,
          maxResultsPerRequests: data.maxResultsPerRequests,
          transforms: data.transforms,
        },
        connectionKey,
      ),
    )
    .then(({ data }) => {
      successToast({
        messageKey: data.enabled
          ? 'ADMIN.DATASOURCES.CONNECTION_HAS_BEEN_ENABLED'
          : 'ADMIN.DATASOURCES.CONNECTION_HAS_BEEN_DISABLED',
      });
    })
    .catch((error) => {
      errorToast({ httpResponseOrError: error, displayForbidden: true });
    });
}

export function updateDatasourceEnabled(
  datasource: DatasourceSummaryStatusOutputV1,
  datasourceEnabled: boolean,
): Promise<unknown> {
  if (datasourceEnabled === true) {
    return Promise.all(
      (datasource.connections ?? []).map((connection) => setConnectionEnabled(connection, datasourceEnabled)),
    );
  } else if (datasourceEnabled === false) {
    return sqDatasourcesApi.disableConnections({ id: datasource.id });
  }
  throw new Error('Implementation error: datasourceEnabled must be true or false.');
}

export async function fullyArchiveDatasource(id: string): Promise<void> {
  // The fullyArchiveDatasource endpoint returns a 202 Accepted status code and conducts the archival async, but we
  // do not currently have a follow-up promise for the datasource archival job (see CRAB-35122).
  try {
    await sqDatasourcesApi.fullyArchiveDatasource({ id });
    successToast({ messageKey: 'ADMIN.DATASOURCES.DATASOURCE_ARCHIVAL_HAS_STARTED' });
  } catch (error) {
    errorToast({ httpResponseOrError: error });
  }
}

export interface ParsedConnectionOutputV1 extends Omit<
  ConnectionOutputV1,
  'json' | 'configuredFieldsJson' | 'additionalConfigJson' | 'transforms'
> {
  json: Record<string, any>;
  configFieldTree: ComplexObjectConfigFieldV1;
  additionalConfigJson: Record<string, any>;
  transforms?: Record<string, any> | null;
}
export async function getParsedConnection(
  agentName: string,
  connectorName: string,
  connectionName: string,
): Promise<ParsedConnectionOutputV1> {
  const retrievedConnection = (await sqAgentsApi.getConnection({ agentName, connectorName, connectionName })).data;
  const returnParsedConnection: ParsedConnectionOutputV1 = {
    ...retrievedConnection,
    json: JSON.parse(retrievedConnection.json ?? '{}'),
    configFieldTree: {
      type: TypeEnum.ComplexObjectConfigFieldV1,
      path: '',
      allowNull: false,
      uniqueKey: '1',
    } as ComplexObjectConfigFieldV1,
    additionalConfigJson: {},
    transforms: undefined,
  };

  try {
    if (_.has(retrievedConnection, 'configuredFieldsJson')) {
      returnParsedConnection.configFieldTree = JSON.parse(retrievedConnection.configuredFieldsJson ?? '{}');
    }
  } catch {
    logError(retrievedConnection.configuredFieldsJson ?? '');
  }
  try {
    if (_.has(retrievedConnection, 'additionalConfigJson')) {
      returnParsedConnection.additionalConfigJson = JSON.parse(retrievedConnection.additionalConfigJson ?? '{}');
    }
  } catch {
    logError(retrievedConnection.additionalConfigJson ?? '');
  }
  try {
    if (_.isString(retrievedConnection.transforms) && _.trim(retrievedConnection.transforms) !== '') {
      returnParsedConnection.transforms = JSON.parse(retrievedConnection.transforms);
    }
  } catch {
    logError(retrievedConnection.transforms ?? '');
  }

  return returnParsedConnection;
}

export function getConnectionNames(
  agentName: string,
  connectorName: string,
): Promise<Array<{ value: string; label: string }>> {
  return sqAgentsApi
    .getConnector({ agentName, connectorName })
    .then(({ data }) => data)
    .then((data: ConnectorOutputV1) => {
      let jsonObj;
      try {
        jsonObj = JSON.parse(data.json ?? '{}');
      } catch (e) {
        return [];
      }

      if (jsonObj.DatasourceManaged) {
        return Promise.reject({
          data: {
            statusMessage: i18next.t('ADMIN.DATASOURCES.CONNECTION_MODAL.CONNECTOR_IS_DATASOURCE_MANAGED'),
          },
        });
      }

      if (!_.isNil(jsonObj.Connections)) {
        return _.map(jsonObj.Connections, (connection: { Name: string }) => {
          return { value: connection.Name, label: connection.Name };
        });
      }
      return [];
    })
    .catch((error: any) => {
      return Promise.reject(_.get(error, 'data.statusMessage'));
    });
}

export function getConnectorNames(
  agentName: string,
  filter?: (c: { Name: string; Enabled: boolean }) => boolean,
): Promise<{ label: string; value: string }[]> {
  return sqAgentsApi
    .getAgent({ agentName })
    .then(({ data }) => data)
    .then((data: ConnectionOutputV1) => {
      let jsonObj;
      try {
        jsonObj = JSON.parse(data.json ?? '{}');
      } catch (e) {
        return [];
      }
      if (!_.isNil(jsonObj.Connectors)) {
        return _.map(_.filter(jsonObj.Connectors, filter), (connector) => {
          return { value: connector.Name, label: connector.Name };
        });
      }
      return [];
    })
    .catch((error: any) => {
      errorToast({ httpResponseOrError: error, displayForbidden: true });
      return [];
    });
}

export async function createOrUpdateConnection(
  isNew: boolean,
  agentName: string,
  datasourceId: string,
  connection: ParsedConnectionOutputV1,
): Promise<AxiosResponse<ConnectionOutputV1, any>> {
  const connectorName = connection.connectorName as string;
  const connectionName = connection.name as string;

  // Initialize the update call's body from the connection's json field
  let callBodyJson = _.cloneDeep(connection.json);
  delete callBodyJson.FieldConfigurations;

  // Process the configFieldTree
  let configValues: any;
  try {
    configValues = await processConfigValuesFromConfigFieldTree(connection.configFieldTree, (secret) =>
      storeOrDeleteConnectionSecret(secret, agentName, connectorName, connectionName),
    );
  } catch (error: any) {
    logError(error);
    throw error; // Rethrow the error so the calling frontend can display a warning to the user
  }

  if (configValues) {
    callBodyJson = { ...callBodyJson, ...configValues };
  }

  if (connection.additionalConfigJson) {
    callBodyJson = { ...callBodyJson, ...connection.additionalConfigJson };
  }

  // Marshal the transforms into a string
  let callTransforms: string | undefined;
  if (!_.isNil(connection.transforms) && _.isArray(connection.transforms) && connection.transforms.length > 0) {
    callTransforms = JSON.stringify(connection.transforms);
  }

  // Marshal the call body json and make the createOrUpdateConnection call
  const body: ConnectionInputV1 = {
    transforms: callTransforms,
    json: JSON.stringify(callBodyJson),
  };
  if (_.isNil(callTransforms)) {
    (body as { transforms: string | null }).transforms = null;
  }
  const includeOptionalFields = !_.isEmpty(connection.additionalConfigJson);
  if (includeOptionalFields || !_.isNil(connection.maxConcurrentRequests)) {
    body.maxConcurrentRequests = connection.maxConcurrentRequests;
  }
  if (includeOptionalFields || !_.isNil(connection.maxResultsPerRequests)) {
    body.maxResultsPerRequests = connection.maxResultsPerRequests;
  }
  if (includeOptionalFields || !_.isNil(connection.enabled)) {
    body.enabled = connection.enabled;
  }
  if (isNew) {
    body.datasourceId = datasourceId;
  }

  return sqAgentsApi.createOrUpdateConnection(body, {
    agentName,
    connectorName,
    connectionName,
  });
}

async function storeOrDeleteConnectionSecret(
  configField: SecretConfigFieldV1,
  agentName: string,
  connectorName: string,
  connectionName: string,
) {
  if (configField.value === SeeqNames.Connectors.Connections.SecretFieldPlaceholderValue) {
    return;
  }

  if (!_.isEmpty(configField.value)) {
    await sqAgentsApi.storeSecretForConnection(
      { key: configField.path, value: configField.value },
      { agentName, connectorName, connectionName },
    );
  } else if (configField.secretStatus) {
    // Delete the secret, since the agent supports getting the secret's status and the new value is null or blank.
    // We delete even when 'secretStatus.hasValue' was already 'false' because:
    // - We may be creating a new connection with the same name as an archived one and need to overwrite any
    // previously created secrets with temporary keys (based on connector and connection names, which are the same)
    // - A secret may have been stored in the agent in the meantime since the modal was opened. If we don't try to
    // delete the result won't match what the user set in the modal.
    await sqAgentsApi.deleteSecretForConnection({
      agentName,
      connectorName,
      connectionName,
      secret: configField.path,
    });
  }
}

async function processConfigValuesFromConfigFieldTree(
  configField: ConfigFieldV1,
  handleSecret: (secret: SecretConfigFieldV1) => Promise<void>,
): Promise<any> {
  if (configField.type === TypeEnum.ComplexObjectConfigFieldV1) {
    const value: Record<string, any> = {};
    for (const [subFieldName, subField] of Object.entries((configField.value || {}) as Record<string, ConfigFieldV1>)) {
      value[subFieldName] = await processConfigValuesFromConfigFieldTree(subField, handleSecret);
    }
    return value;
  } else if (configField.type === TypeEnum.ListConfigFieldV1) {
    const value = [];
    for (const element of (configField.value || []) as ConfigFieldV1[]) {
      value.push(await processConfigValuesFromConfigFieldTree(element, handleSecret));
    }
    return value;
  } else if (configField.type === TypeEnum.SecretConfigFieldV1) {
    await handleSecret(configField as SecretConfigFieldV1);
    return null; // Secret values are not stored in the connection configuration JSON
  } else {
    return configField.value;
  }
}

// Simple data class for helping to track array configuration fields and a field's current index in the array while
// rendering
export class ListIndex {
  name: string;
  index: number;

  constructor(name: string, index: number) {
    this.name = name;
    this.index = index;
  }
}

// Insert the tracked index values for the specified path
export function pathIndexHelper(path: string, listIndexes: ListIndex[]): string {
  if (listIndexes.length > 0) {
    let splitPath = path.split('.');
    for (let i = 0; i < splitPath.length; i++) {
      for (let listIndex of listIndexes) {
        if (splitPath[i] === listIndex.name) {
          splitPath[i] = `${splitPath[i]}[${listIndex.index.toString()}]`;
        }
      }
    }
    return splitPath.join('.');
  } else {
    return path;
  }
}

/**
 * Sorts the provided list of connections in the correct order the frontend needs:
 * - First disconnected
 * - Then connecting
 * - Then indexing
 * - Then connected
 * - Then disabled
 */
export function sortConnections(connections: ConnectionStatusOutputV1[]): ConnectionStatusOutputV1[] | null {
  if (_.isNil(connections)) {
    return null;
  }

  let unprocessed = connections;

  const disconnected = _.sortBy(
    _.filter(unprocessed, (c) => getConnectionStatus(c) === ConnectionStatus.Disconnected),
    'name',
  );
  unprocessed = _.difference(unprocessed, disconnected);

  const connecting = _.sortBy(
    _.filter(unprocessed, (c) => getConnectionStatus(c) === ConnectionStatus.Connecting),
    'name',
  );
  unprocessed = _.difference(unprocessed, connecting);

  const indexing = _.sortBy(
    _.filter(unprocessed, (c) => getConnectionStatus(c) === ConnectionStatus.Indexing),
    'name',
  );
  unprocessed = _.difference(unprocessed, indexing);

  const connected = _.sortBy(
    _.filter(unprocessed, (c) => getConnectionStatus(c) === ConnectionStatus.Connected),
    'name',
  );
  unprocessed = _.difference(unprocessed, connected);

  const disabled = _.sortBy(
    _.filter(unprocessed, (c) => getConnectionStatus(c) === ConnectionStatus.Disabled),
    'name',
  );
  unprocessed = _.sortBy(_.difference(unprocessed, disabled), 'name');

  return _.concat(disconnected, connecting, indexing, connected, disabled, unprocessed);
}

export async function removeConnection(connection: ConnectionStatusOutputV1) {
  try {
    await sqAgentsApi.archiveConnection({
      agentName: connection.agentName,
      connectorName: connection.connectorName as string,
      connectionName: connection.name as string,
    });
    successToast({
      messageKey: 'ADMIN.DATASOURCES.CONNECTION_HAS_BEEN_REMOVED',
    });
  } catch (error) {
    errorToast({ httpResponseOrError: error });
  }
}

export function isConnectionRemovable(
  connection: ConnectionStatusOutputV1,
  datasource: DatasourceSummaryStatusOutputV1,
): boolean {
  return (
    isPlaceholder(datasource) ||
    ((datasource?.connections?.length ?? 0) > 1 &&
      connection.status !== SeeqNames.Connectors.Connections.Status.Connected &&
      connection.status !== SeeqNames.Connectors.Connections.Status.Connecting)
  );
}

export function getConnectionStatus(connection: ConnectionStatusOutputV1): ConnectionStatus {
  if (_.isNil(connection)) {
    return ConnectionStatus.Unknown;
  }

  if (connection.status === SeeqNames.Connectors.Connections.Status.Disconnected) {
    return ConnectionStatus.Disconnected;
  } else if (connection.status === SeeqNames.Connectors.Connections.Status.Disabled) {
    return ConnectionStatus.Disabled;
  } else if (connection.status === SeeqNames.Connectors.Connections.Status.Connecting) {
    return ConnectionStatus.Connecting;
  } else if (connection.status === SeeqNames.Connectors.Connections.Status.Connected) {
    if (connection.syncStatus === SyncStatusEnum.INPROGRESS) {
      return ConnectionStatus.Indexing;
    } else {
      return ConnectionStatus.Connected;
    }
  } else {
    return ConnectionStatus.Unknown;
  }
}

/**
 * Computes the url where the logs of the agent can be retrieved.
 */
export function computeLogUrl(
  agentName: string,
  agents: AgentStatusOutputV1[],
  connection?: ConnectionStatusOutputV1,
): string {
  const agent = _.find(agents, (a) => a.name === agentName);

  let logName;
  if (_.isNil(agent)) {
    logName = null;
  } else if (agent.remoteAgent) {
    // Keep this in sync with RemoteAgentLoggingService
    logName = agent.name
      .replace(/[\\/:*?"<>|]/g, '')
      .replace(/[.\s]/g, '_')
      .replace(/(\d)$/g, '$1_');
  } else if (_.includes(agent.name, 'JVM Agent')) {
    logName = 'jvm-link';
  } else if (_.includes(agent.name, '.NET Agent')) {
    logName = 'net-link';
  } else {
    logName = null;
  }

  let url = '/logs';

  if (!_.isNil(logName)) {
    url += `?log=${encodeURIComponent(logName)}`;
  }

  if (!_.isNil(connection) && !_.isNil(connection.lastSuccessfulConnectedAt)) {
    if (_.isNil(logName)) {
      url += '?';
    } else {
      url += '&';
    }

    url += `threadContains=${encodeURIComponent(connection.connectionId)}`;
  }

  return url;
}

/**
 * Fetch the SCIM token and its details for the specified datasource.
 *
 * @param datasourceClass - The class of the datasource from which the token is being fetched.
 * @param datasourceId - The unique identifier of the datasource.
 * @returns A promise that resolves to the SCIM token parameters
 */
export async function fetchScimToken(datasourceClass: string, datasourceId: string): Promise<ScimTokenParams> {
  try {
    const {
      data: { expiration, authToken },
    } = await sqSCIMApi.getToken({ datasourceClass, datasourceId });

    return {
      scimTokenExpiration: expiration,
      scimAuthToken: authToken,
      scimEnabled: true,
    };
  } catch (error: any) {
    // if error is 404, the datasource does not support SCIM
    if (error.response?.status !== 404) {
      throw error;
    }
    return {
      scimTokenExpiration: null,
      scimAuthToken: null,
      scimEnabled: false,
    };
  }
}

/**
 * Generate a new SCIM token with the specified validity for the datasource.
 *
 * @param datasourceClass - The class of the datasource for which the token is generated.
 * @param datasourceId - The unique identifier of the datasource.
 * @param tokenValidityDuration - The duration (in days) for which the token remains valid.
 *                                If not provided, the default validity is 90 days.
 * @returns A promise that resolves to the generated SCIM token parameters.
 */
export async function generateScimToken(
  datasourceClass: string,
  datasourceId: string,
  tokenValidityDuration: number,
): Promise<ScimTokenParams> {
  const {
    data: { expiration, authToken },
  } = await sqSCIMApi.generateToken({ tokenValidityDuration }, { datasourceClass, datasourceId });

  return {
    scimTokenExpiration: expiration,
    scimAuthToken: authToken,
    scimEnabled: true,
  };
}

/**
 * Disable SCIM for the specified datasource by invalidating the SCIM token.
 *
 * @param datasourceClass - The class of the datasource for which SCIM is being disabled.
 * @param datasourceId - The unique identifier of the datasource.
 * @returns A promise that resolves once the SCIM token has been invalidated.
 */
export async function disableScim(datasourceClass: string, datasourceId: string): Promise<void> {
  await sqSCIMApi.invalidateToken({ datasourceClass, datasourceId });
}

/**
 * Fetch parameters required by the ManageDatasourceModal in the format it expects
 *
 * @param datasource - the datasource
 */
export async function fetchManageDatasourceParams(
  datasource: DatasourceSummaryStatusOutputV1,
): Promise<ManageDatasourceParams | void> {
  try {
    const {
      data: { name, datasourceClass, indexingScheduleSupported, additionalProperties },
    } = await sqDatasourcesApi.getDatasource({ id: datasource.id });
    const indexingFrequency = _.chain(additionalProperties)
      .filter(['name', SeeqNames.Properties.IndexingFrequency])
      .map((prop) => ({ value: prop.value, units: prop.unitOfMeasure }))
      .first()
      .value();
    const nextScheduledIndexAt = _.chain(additionalProperties)
      .filter(['name', SeeqNames.Properties.NextScheduledIndexAt])
      .map((prop) => moment.utc(prop.value / NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND).toISOString())
      .first()
      .value();
    const maxRequests =
      _.chain(additionalProperties)
        .filter(['name', SeeqNames.Properties.MaxRequests])
        .map((prop) => prop.value)
        .first()
        .value() ?? null;
    // If the property is not set we consider it to be true for backwards compatibility with existing datasources
    const providesEveryoneGroupIdentities =
      _.find(additionalProperties, ['name', SeeqNames.Properties.ProvidesEveryoneGroupIdentities])?.value !== false;
    const isIdentityProvider = _.includes(SeeqNames.IdentityProviderDatasourceClasses, datasourceClass);
    const scimMappingGroupExternalId =
      _.find(additionalProperties, ['name', SeeqNames.Properties.ScimMappingGroupExternalId])?.value ===
      SeeqNames.Properties.ScimMappingObjectId
        ? SeeqNames.Properties.ScimMappingObjectId
        : SeeqNames.Properties.ScimMappingSecurityId;

    return {
      id: datasource.id,
      name,
      datasourceClass: datasource.datasourceClass,
      datasourceId: datasource.datasourceId,
      isIdentityProvider,
      indexingScheduleSupported: indexingScheduleSupported ?? false,
      indexingFrequency: indexingFrequency
        ? { value: Number(indexingFrequency.value), units: indexingFrequency.units ?? '' }
        : undefined,
      nextScheduledIndexAt,
      providesEveryoneGroupIdentities,
      maxRequests,
      connections: datasource.connections,
      datasourceLabels: parseDatasourceLabelsProperty(datasource.datasourceLabels),
      scimMappingGroupExternalId,
      scimTokenParams: isIdentityProvider
        ? await fetchScimToken(datasource.datasourceClass ?? '', datasource.datasourceId ?? '')
        : {
            scimTokenExpiration: null,
            scimAuthToken: null,
            scimEnabled: false,
          },
    };
  } catch (error) {
    errorToast({ httpResponseOrError: error });
  }
}

/**
 * Update the datasource
 *
 * @param id - the datasource ID
 * @param name - the new name of the datasource
 * @param indexingFrequency - the new indexing frequency
 * @param nextScheduledIndexAt - the new next scheduled indexing time, formatted as ISO 8601 in UTC
 * @param providesEveryoneGroupIdentities - a boolean representing whether the datasource provides Everyone group
 * identities
 * @param datasourceLabels - an array of strings to be used as labels for the datasource. If nil, no action
 * will be taken; if an empty list, deleteProperty will be called to remove the existing property
 * @param connections - the list of connections of the datasource
 * @param connectionsEnabled - a boolean representing the new global enabled state of the connections
 * @param connectionsEnabledWasUpdated - boolean representing whether the connections' enabled status should be updated
 * @param maxRequests - the max number of requests allowed to the datasource at once; null means unlimited
 * @param scimMappingGroupExternalId - the source ID field external ID is recorded in
 */
export async function updateDatasource({
  id,
  name,
  indexingFrequency,
  nextScheduledIndexAt,
  providesEveryoneGroupIdentities,
  datasourceLabels,
  connections,
  connectionsEnabled,
  connectionsEnabledWasUpdated,
  maxRequests,
  scimMappingGroupExternalId,
}: UpdateDatasourceParams): Promise<unknown> {
  const properties: ScalarPropertyV1[] = [
    {
      name: SeeqNames.Properties.Name,
      value: name,
    },
  ];

  if (!_.isNil(providesEveryoneGroupIdentities)) {
    properties.push({
      name: SeeqNames.Properties.ProvidesEveryoneGroupIdentities,
      value: providesEveryoneGroupIdentities,
    });
  }

  if (!_.isUndefined(nextScheduledIndexAt)) {
    properties.push({
      name: SeeqNames.Properties.NextScheduledIndexAt,
      value: moment.utc(nextScheduledIndexAt).valueOf() * NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND,
      unitOfMeasure: 'ns',
    });
  }

  if (!_.isUndefined(indexingFrequency)) {
    properties.push({
      name: SeeqNames.Properties.IndexingFrequency,
      value: indexingFrequency.value,
      unitOfMeasure: indexingFrequency.units,
    });
  }

  if (!_.isNil(datasourceLabels)) {
    const datasourceLabelsProperty = stringifyDatasourceLabels(datasourceLabels);
    if (_.trim(datasourceLabelsProperty)) {
      properties.push({
        name: SeeqNames.Properties.DatasourceLabels,
        value: datasourceLabelsProperty,
      });
    } else {
      await sqItemsApi.deleteProperty({ id, propertyName: SeeqNames.Properties.DatasourceLabels });
    }
  }

  if (!_.isNull(maxRequests)) {
    properties.push({
      name: SeeqNames.Properties.MaxRequests,
      value: maxRequests,
    });
  } else {
    try {
      await sqItemsApi.deleteProperty({ id, propertyName: SeeqNames.Properties.MaxRequests });
    } catch (error: any) {
      // The property might not exist; we can ignore this error safely
      if (!error.data?.statusMessage?.match(/The property .* could not be found/)) {
        errorToast({ httpResponseOrError: error });
      }
    }
  }

  if (!_.isUndefined(scimMappingGroupExternalId)) {
    properties.push({
      name: SeeqNames.Properties.ScimMappingGroupExternalId,
      value: scimMappingGroupExternalId,
    });
  }

  if (connectionsEnabledWasUpdated) {
    // Handles the case where the datasource has multiple connections in the backend but the frontend only sees one
    if (!connectionsEnabled) {
      await sqDatasourcesApi.disableConnections({ id });
    } else {
      await Promise.all([
        ..._.map(connections ?? [], (connection) => setConnectionEnabled(connection, connectionsEnabled)),
      ]);
    }
  }
  return sqItemsApi.setProperties(properties, { id });
}

export function getTrackableMessage(datasourcesStatus: DatasourcesStatusOutputV1) {
  if (_.isNil(datasourcesStatus)) {
    return datasourcesStatus;
  }
  const agents = _.isNil(datasourcesStatus.agents)
    ? datasourcesStatus.agents
    : _.map(datasourcesStatus.agents, (agent) => _.pick(agent, ['name', 'status']));

  const datasources = _.isNil(datasourcesStatus.datasources)
    ? datasourcesStatus.datasources
    : _.map(datasourcesStatus.datasources, (datasource) => {
        const relevantFields = _.pick(datasource, ['id', 'name', 'datasourceClass', 'datasourceId', 'syncProgress']);
        relevantFields.syncProgress = _.pick(relevantFields.syncProgress, [
          'signalCount',
          'conditionCount',
          'scalarCount',
          'assetCount',
          'userGroupCount',
        ]);
        return relevantFields;
      });

  return { agents, datasources };
}

export function datasourcesChanged(
  datasources: DatasourcesStatusOutputV1 | undefined,
  previousDatasourcesIds: string[],
): boolean {
  if (_.isNil(datasources)) {
    return false;
  }
  return !_.isEmpty(
    _.xor(
      _.map(datasources.datasources ?? [], (d) => d.id),
      previousDatasourcesIds,
    ),
  );
}

export function getDatasourceIds(datasources: DatasourcesStatusOutputV1): string[] {
  return _.map(datasources.datasources ?? [], (datasource) => datasource.id);
}

export function parseDatasourceLabelsProperty(datasourceLabelsProperty: string | null | undefined): string[] {
  return _.isNil(datasourceLabelsProperty) ? [] : _.split(datasourceLabelsProperty, ';');
}

function stringifyDatasourceLabels(datasourceLabels: string[]): string {
  return _.join(_.sortBy(_.map(datasourceLabels, (dsLabel) => _.trim(dsLabel))), ';');
}

export function isLabelValid(datasourceLabel: string): boolean {
  return !!_.trim(datasourceLabel) && !_.includes(datasourceLabel, ';');
}

export function dedupLabels(labels: string[]): string[] {
  return [
    ...new Set(
      _.chain(labels)
        .map(_.trim)
        .filter((dsLabel) => !!_.trim(dsLabel))
        .value(),
    ),
  ];
}

export function getDatasourceLabelOptions(datasources: DatasourcesStatusOutputV1): string[] {
  return [
    ...new Set(
      _.flatMap(_.map(datasources?.datasources ?? [], (ds) => parseDatasourceLabelsProperty(ds.datasourceLabels))),
    ),
  ];
}

export function addDatasourceLabelOptions(
  params: ManageDatasourceParams | void,
  datasourceLabelOptions: string[],
): ManageDatasourceParams | void {
  return _.assign({}, params, { datasourceLabelOptions });
}

export function clearDatasourceCache(datasource: DatasourceSummaryStatusOutputV1): Promise<void> {
  return sqDatasourcesApi
    .clearDatasourceCache({ id: datasource.id, recompileFormulas: false })
    .then(() => {
      // Successfully cleared cache - no action needed
    })
    .catch((error: any) => {
      errorToast({ httpResponseOrError: error, displayForbidden: true });
    });
}

export function getDatasourceItemsCount(datasource: DatasourceSummaryStatusOutputV1): number {
  const syncProgress = datasource.syncProgress;

  return _.isNil(syncProgress)
    ? 0
    : (syncProgress.signalCount || 0) +
        (syncProgress.conditionCount || 0) +
        (syncProgress.scalarCount || 0) +
        (syncProgress.assetCount || 0) +
        (syncProgress.userGroupCount || 0);
}

export function getPreviousDatasourceItemsCount(datasource: DatasourceSummaryStatusOutputV1): number {
  const syncProgress = datasource.syncProgress;

  return _.isNil(syncProgress)
    ? 0
    : (syncProgress.previousSignalCount || 0) +
        (syncProgress.previousConditionCount || 0) +
        (syncProgress.previousScalarCount || 0) +
        (syncProgress.previousAssetCount || 0) +
        (syncProgress.previousUserGroupCount || 0);
}

function isHiddenFromNewConnectionDropdown(name: string) {
  if (!name) return false;
  return [
    'Add-on Calculation Python Connector',
    'External Calculation Python Connector',
    'Add-on Calculation Excel VBA Connector',
    'External Calculation Excel VBA Connector',
    'Excel VBA Connector',
    'FileFolders Connector',
  ].includes(name);
}

export function isConnectorDisabledExplicitly(name: string) {
  return isHiddenFromNewConnectionDropdown(name);
}

export interface UpdateDatasourceParams {
  id: string;
  name: string;
  indexingFrequency?: { value: number; units: string };
  nextScheduledIndexAt?: string; // ISO 8601 in UTC (e.g. '2021-07-10T00:24:00.000Z')
  datasourceLabels?: string[] | null;
  connections?: ConnectionStatusOutputV1[];
  connectionsEnabled?: boolean;
  connectionsEnabledWasUpdated?: boolean;
  providesEveryoneGroupIdentities?: boolean;
  providesEveryoneGroupIdentitiesWasUpdated?: boolean;
  maxRequests: number | null;
  scimMappingGroupExternalId?: ScimMappingGroupExternalId;
}

export interface ManageDatasourceParams extends UpdateDatasourceParams {
  indexingScheduleSupported: boolean;
  isIdentityProvider?: boolean;
  datasourceLabelOptions?: string[];
  scimTokenParams: ScimTokenParams;
  datasourceClass: string;
  datasourceId: string;
}

export interface ScimTokenParams {
  scimTokenExpiration?: string | null;
  scimAuthToken?: string | null;
  scimEnabled: boolean;
}

export type ScimMappingGroupExternalId =
  | typeof SeeqNames.Properties.ScimMappingSecurityId
  | typeof SeeqNames.Properties.ScimMappingObjectId;

export interface DatasourceFilterParameters {
  name: string;
  datasourceClass: string;
  datasourceId: string;
  agentName: string;
  status: string;
  metricsTimeRange: string;
  datasourceLabels: string[];
}

/**
 * Serializes filter parameters to URLSearchParams for use in URL query strings.
 * Empty strings are omitted for cleaner URLs.
 */
export function serializeDatasourceFiltersToUrl(filters: Partial<DatasourceFilterParameters>): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.name) params.set('name', filters.name);
  if (filters.datasourceClass) params.set('datasourceClass', filters.datasourceClass);
  if (filters.datasourceId) params.set('datasourceId', filters.datasourceId);
  if (filters.agentName) params.set('agentName', filters.agentName);
  if (filters.status) params.set('status', filters.status);
  if (filters.metricsTimeRange) params.set('metricsTimeRange', filters.metricsTimeRange);

  // Handle array values - add multiple params with same key
  if (filters.datasourceLabels && filters.datasourceLabels.length > 0) {
    filters.datasourceLabels.forEach((label) => {
      if (label) params.append('datasourceLabels', label);
    });
  }

  return params;
}

/**
 * Deserializes URLSearchParams to DatasourceFilterParameters with default values.
 * Returns a complete DatasourceFilterParameters object with defaults for missing values.
 */
export function deserializeDatasourceFiltersFromUrl(searchParams: URLSearchParams): DatasourceFilterParameters {
  const defaultMetricsTimeRange = `3 ${i18next.t('ADMIN.DATASOURCES.TIME_RANGE_DEFAULT_UNITS')}`;

  return {
    name: searchParams.get('name') || '',
    datasourceClass: searchParams.get('datasourceClass') || '',
    datasourceId: searchParams.get('datasourceId') || '',
    agentName: searchParams.get('agentName') || '',
    status: searchParams.get('status') || '',
    metricsTimeRange: searchParams.get('metricsTimeRange') || defaultMetricsTimeRange,
    datasourceLabels: searchParams.getAll('datasourceLabels').filter((label) => !_.isEmpty(label)),
  };
}

export interface DatasourceMetrics {
  DatasourceSeeqId: string;
  SuccessCount: number;
  FailureCount: number;
  SampleCount: number;
  CapsuleCount: number;
  AverageRequestTime: number;
  TotalRequestCount: number;
}

export interface DatasourceMetricsContainer {
  Last24Hours: DatasourceMetrics;
  LastWeek: DatasourceMetrics;
}

export interface DatasourcesMetrics {
  metrics: Map<string, DatasourceMetricsContainer>;
}

export enum DatasourceStatus {
  Unknown = 'Unknown',
  New = 'New',
  Error = 'Error',
  Indexing = 'Indexing',
  Warning = 'Warning',
  Happy = 'Happy',
  NotConnectable = 'NotConnectable',
}

export enum ConnectionStatus {
  Unknown = 'Unknown',
  Disconnected = 'Disconnected',
  Connecting = 'Connecting',
  Connected = 'Connected',
  Indexing = 'Indexing',
  Disabled = 'Disabled',
}

export const datasourcesStatusMessages = new Map([
  [DatasourceStatus.Unknown, 'DATASOURCES.DISCONNECTED'],
  [DatasourceStatus.New, 'DATASOURCES.PENDING_DATASOURCE'],
  [DatasourceStatus.Error, 'DATASOURCES.DISCONNECTED'],
  [DatasourceStatus.Warning, 'DATASOURCES.INDEX_SUMMARY'],
  [DatasourceStatus.Indexing, 'DATASOURCES.INDEX_PROGRESS'],
  [DatasourceStatus.Happy, 'DATASOURCES.INDEX_SUMMARY'],
  [DatasourceStatus.NotConnectable, 'DATASOURCES.DISABLED'],
]);

const DatasourceStatusIcons = new Map([
  [DatasourceStatus.Unknown, 'fa-exclamation-triangle sq-status-error'],
  [DatasourceStatus.New, 'fa-question-circle sq-status-info'],
  [DatasourceStatus.Error, 'fa-exclamation-triangle sq-status-error'],
  [DatasourceStatus.Indexing, 'loading-spinner loading-spinner-icon sq-status-progress'],
  [DatasourceStatus.Warning, 'fa-exclamation-circle sq-status-warning'],
  [DatasourceStatus.Happy, 'fa-check-circle sq-status-good'],
  [DatasourceStatus.NotConnectable, 'fa-minus-circle disabledLook'],
]);

const ConnectionStatusIcons = new Map([
  [ConnectionStatus.Unknown, 'fa-exclamation-triangle sq-status-error width-17'],
  [ConnectionStatus.Disconnected, 'fa-exclamation-triangle sq-status-error width-17'],
  [ConnectionStatus.Connecting, 'loading-spinner loading-spinner-icon sq-status-progress width-17'],
  [ConnectionStatus.Connected, 'fa-check-circle sq-status-good width-17'],
  [ConnectionStatus.Indexing, 'loading-spinner loading-spinner-icon sq-status-progress width-17'],
  [ConnectionStatus.Disabled, 'fa-minus-circle disabledLook width-17'],
]);

export { ConnectionStatusIcons, DatasourceStatusIcons };

export const DATASOURCE_MONITORS_METRICS_DATA_IDS = {
  RxSuccessesMeter: 'Rx/Successes.Meter',
  RxFailuresMeter: 'Rx/Failures.Meter',
  RxSamplesMeter: 'Rx/Samples.Meter',
  RxCapsulesMeter: 'Rx/Capsules.Meter',
  RequestTimeTimerMean: 'RequestTime.Timer.mean',
  RequestTimeTimerCount: 'RequestTime.Timer.count',
} as const;
